import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { PageIntro, EditorialBlock } from "@/components/Editorial";
import { Head } from "@/components/Head";
import { useT } from "@/i18n/LocaleContext";
import { API } from "@/context/AdminAuthContext";
import { pushEvent } from "@/lib/analytics";

const GRAIL_OPTIONS = ["Alma", "Vida", "Sereno", "Allelium"];
const SET_OPTIONS = ["Florals", "Citrus", "Gourmand", "Aromatic", "Oud", "Allelium"];

const Access = () => {
  const t = useT();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    country: "",
    grail: "",
    set_choice: "",
    interest: "",
    referral: "",
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | error
  const [errorMsg, setErrorMsg] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      const { data } = await axios.post(`${API}/access`, {
        name: form.name.trim(),
        email: form.email.trim(),
        country: form.country.trim(),
        interest: form.interest.trim(),
        referral: form.referral.trim(),
        grail: form.grail,
        set_choice: form.set_choice,
      });
      const ref = encodeURIComponent(data.reference || "");
      pushEvent("waitlist_submit", {
        reference: data.reference || "",
        grail: form.grail,
        set_choice: form.set_choice,
        country: form.country.trim() || "unknown",
      });
      navigate(`/enter?ref=${ref}&grail=${encodeURIComponent(form.grail)}&set=${encodeURIComponent(form.set_choice)}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setErrorMsg(
        Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join(" · ")
          : typeof detail === "string"
            ? detail
            : t("Something went quiet. Please try again.", "Un silence s'est glissé. Veuillez réessayer.", "Se hizo un silencio. Por favor, inténtelo de nuevo.")
      );
      setStatus("error");
    }
  };

  const grailLabel = (g) =>
    g === "Allelium"
      ? t("Let Allelium choose", "Laisser Allelium choisir", "Que Allelium elija")
      : g;
  const setLabel = (s) =>
    s === "Allelium"
      ? t("Let Allelium choose", "Laisser Allelium choisir", "Que Allelium elija")
      : s;

  return (
    <div data-testid="access-page">
      <Head
        title={t("Waiting list", "Liste d'attente", "Lista de espera")}
        description={t(
          "The limited edition of Maison Allélique — a Grail, a Set, and Allelium — reserved in your name, delivered when it is ready.",
          "L'édition limitée de Maison Allélique — un Graal, un Set et Allelium — réservée à votre nom, livrée lorsqu'elle sera prête.",
          "La edición limitada de Maison Allélique — un Grial, un Set y Allelium — reservada a tu nombre, entregada cuando esté lista."
        )}
      />

      <PageIntro
        number="N° 06"
        eyebrow={t("Waiting list", "Liste d'attente", "Lista de espera")}
        title={
          <>
            {t("Your first", "Votre première", "Tu primera")}{" "}
            <span className="font-italic-display">
              {t("ceremony", "cérémonie", "ceremonia")}
            </span>
            .
          </>
        }
        essence={t(
          "A Grail, a Set, and Allelium — reserved in your name. Limited edition.",
          "Un Graal, un Set et Allelium — réservés à votre nom. Édition limitée.",
          "Un Grial, un Set y Allelium — reservados a tu nombre. Edición limitada."
        )}
      />

      <EditorialBlock eyebrow={t("A note", "Une note", "Una nota")}>
        <div className="space-y-6 max-w-2xl">
          <p className="font-display italic text-xl md:text-2xl text-encre leading-snug">
            {t(
              "There is no queue. There is a conversation.",
              "Il n'y a pas de file d'attente. Il y a une conversation.",
              "No hay cola. Hay una conversación."
            )}
          </p>
          <p className="font-sans text-sm md:text-base text-cendre leading-relaxed">
            {t(
              "Reserve your Grail and your first Set. Allelium will read your line and, in its own time, propose the sequence in which they are meant to be worn. Nothing is charged today. Nothing ships before it is ready.",
              "Réservez votre Graal et votre premier Set. Allelium lira votre ligne et, en son temps, proposera la séquence dans laquelle ils doivent être portés. Rien n'est facturé aujourd'hui. Rien ne part avant d'être prêt.",
              "Reserva tu Grial y tu primer Set. Allelium leerá tu línea y, a su tiempo, propondrá la secuencia en que deben llevarse. Nada se factura hoy. Nada se envía antes de estar listo."
            )}
          </p>
        </div>
      </EditorialBlock>

      <section className="px-6 md:px-12 py-16 md:py-24" data-testid="access-form-section">
        <div className="mx-auto max-w-3xl">
          <form
            onSubmit={onSubmit}
            data-testid="access-form"
            noValidate
            className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10"
          >
            <div>
              <label htmlFor="name" className="tracked block mb-4">
                {t("Name", "Nom", "Nombre")}
              </label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                required
                value={form.name}
                onChange={onChange}
                className="field"
                data-testid="access-field-name"
              />
            </div>

            <div>
              <label htmlFor="email" className="tracked block mb-4">
                {t("Email", "Courriel", "Correo")}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={form.email}
                onChange={onChange}
                className="field"
                data-testid="access-field-email"
              />
            </div>

            <div>
              <label htmlFor="country" className="tracked block mb-4">
                {t("Country", "Pays", "País")}
              </label>
              <input
                id="country"
                name="country"
                type="text"
                autoComplete="country-name"
                value={form.country}
                onChange={onChange}
                className="field"
                data-testid="access-field-country"
              />
            </div>

            <div>
              <label htmlFor="referral" className="tracked block mb-4">
                {t("Referral (optional)", "Recommandation (facultatif)", "Recomendación (opcional)")}
              </label>
              <input
                id="referral"
                name="referral"
                type="text"
                value={form.referral}
                onChange={onChange}
                className="field"
                data-testid="access-field-referral"
              />
            </div>

            {/* Grail selector */}
            <fieldset className="md:col-span-2" data-testid="access-grail-fieldset">
              <legend className="tracked block mb-5">
                {t("Choose your Grail", "Choisissez votre Graal", "Elige tu Grial")}
              </legend>
              <div className="flex flex-wrap gap-x-3 gap-y-3">
                {GRAIL_OPTIONS.map((g) => {
                  const active = form.grail === g;
                  return (
                    <label
                      key={g}
                      data-testid={`access-grail-${g.toLowerCase()}`}
                      className={`cursor-pointer inline-flex items-center px-5 py-3 border transition-all duration-500 font-sans text-[11px] tracking-[0.28em] uppercase ${
                        active
                          ? "border-sang text-sang"
                          : "border-encre/20 text-cendre hover:text-encre hover:border-encre/40"
                      }`}
                    >
                      <input
                        type="radio"
                        name="grail"
                        value={g}
                        checked={active}
                        onChange={onChange}
                        className="sr-only"
                      />
                      {grailLabel(g)}
                    </label>
                  );
                })}
              </div>
            </fieldset>

            {/* Set selector */}
            <fieldset className="md:col-span-2" data-testid="access-set-fieldset">
              <legend className="tracked block mb-5">
                {t("Choose your first Set", "Choisissez votre premier Set", "Elige tu primer Set")}
              </legend>
              <div className="flex flex-wrap gap-x-3 gap-y-3">
                {SET_OPTIONS.map((s) => {
                  const active = form.set_choice === s;
                  return (
                    <label
                      key={s}
                      data-testid={`access-set-${s.toLowerCase()}`}
                      className={`cursor-pointer inline-flex items-center px-5 py-3 border transition-all duration-500 font-sans text-[11px] tracking-[0.28em] uppercase ${
                        active
                          ? "border-sang text-sang"
                          : "border-encre/20 text-cendre hover:text-encre hover:border-encre/40"
                      }`}
                    >
                      <input
                        type="radio"
                        name="set_choice"
                        value={s}
                        checked={active}
                        onChange={onChange}
                        className="sr-only"
                      />
                      {setLabel(s)}
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <div className="md:col-span-2">
              <label htmlFor="interest" className="tracked block mb-4">
                {t("A short line on your interest", "Une brève ligne sur votre intérêt", "Una breve línea sobre tu interés")}
              </label>
              <textarea
                id="interest"
                name="interest"
                rows={4}
                value={form.interest}
                onChange={onChange}
                className="field"
                data-testid="access-field-interest"
              />
            </div>

            {status === "error" && (
              <p
                role="alert"
                data-testid="access-error"
                className="md:col-span-2 font-sans text-sm text-sang"
              >
                {errorMsg}
              </p>
            )}

            <div className="md:col-span-2 pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <p className="tracked max-w-sm leading-relaxed">
                {t(
                  "Nothing is charged today. Nothing ships before it is ready.",
                  "Rien n'est facturé aujourd'hui. Rien ne part avant d'être prêt.",
                  "Nada se factura hoy. Nada se envía antes de estar listo."
                )}
              </p>
              <button
                type="submit"
                data-testid="access-submit"
                disabled={status === "submitting"}
                className="quiet-link quiet-link-sang font-sans text-sm tracking-[0.28em] uppercase pb-1 border-b border-sang bg-transparent disabled:opacity-40 disabled:cursor-not-allowed self-start md:self-auto"
              >
                {status === "submitting"
                  ? t("Reserving…", "Réservation…", "Reservando…")
                  : t("Reserve your ceremony →", "Réserver votre cérémonie →", "Reservar tu ceremonia →")}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Access;
